import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import cls from "classnames";
import confetti from "canvas-confetti";
import { Button, PlusLink } from "@clarkinc/zest-design-system/ui";
import { Image } from "@wss/image";
interface WelcomeMessageProps {
  modalID: string;
  onClose?: () => void;
  welcomeMessageIsVisible: boolean;
}
const WelcomeMessage = ({
  modalID,
  onClose,
  welcomeMessageIsVisible
}: WelcomeMessageProps) => {
  const [width, setWidth] = useState(window.innerWidth);
  const [modalInfo, setModalInfo] = useState({
    height: 0,
    bottomOverflow: 0,
    width: 0
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const confettiRef = useRef<ReturnType<typeof confetti.create> | null>(null);
  const plusFlagRef = useRef<confetti.Shape | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [animationHasFired, setAnimationHasFired] = useState(false);

  // Update width for responsive text
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Initialize confetti canvas and custom shape (TODO: get plus flag shape to work)
  useEffect(() => {
    if (!welcomeMessageIsVisible) return;
    const canvas = canvasRef.current;
    const modal = document.getElementById(modalID);
    if (!modal || !canvas) return;

    // devicePixelRatio => tells you how many physical device pixels correspond to 1 CSS pixel.
    // by default, canvas rendering assumes a 1:1 pixel ratio.
    // Using this gives sharper rendering on high-DPI/retina screens.
    const dpr = window.devicePixelRatio || 1;
    const updateCanvasSize = () => {
      const ctx = canvas.getContext("2d");
      canvas.height = modal.clientHeight * dpr;
      canvas.width = modal.clientWidth * dpr;
      if (ctx) ctx.scale(dpr, dpr);
    };
    const updateModalMetrics = () => {
      const {
        scrollHeight,
        clientHeight,
        clientWidth
      } = modal;
      setModalInfo({
        bottomOverflow: scrollHeight - clientHeight,
        height: clientHeight,
        width: clientWidth
      });
      updateCanvasSize();
    };
    updateModalMetrics(); //Initial run

    window.addEventListener("resize", updateModalMetrics);
    return () => {
      window.removeEventListener("resize", updateModalMetrics);
    };
  }, [modalID, welcomeMessageIsVisible]);
  const getConfettiConfig = useCallback(() => {
    const colors = ["#00405D", "#00597E", "#00658E"];
    const color = colors[Math.floor(Math.random() * colors.length)];
    const plusFlag = plusFlagRef.current;
    if (!plusFlag) return; // Ensure the shape is defined

    return {
      colors: color ? [color] : [],
      // Array of colors to use (empty = default fallback)
      drift: Math.random() * 1.2 - 0.6,
      gravity: 1.5,
      // Affects how fast the particle falls (higher = faster drop)
      particleCount: 1,
      // Number of confetti particles per fire
      scalar: width >= 979 ? 6 : 4,
      // Size multiplier for particles based on screen width
      shapes: [plusFlag],
      // Custom shape(s) used for each confetti particle
      spread: 0,
      startVelocity: 30 + Math.random() * 30,
      // Initial velocity (0 = float down slowly)
      ticks: 300 + Math.floor(Math.random() * 100),
      // Lifespan of the particle in frames (higher = lasts longer)
      origin: {
        x: Math.random(),
        // Horizontal origin (0 = left, 1 = right)
        y: 0 // Vertical origin (centered around 0 for more dynamic bursts)
      }
    };
  }, [width]);
  const fireConfetti = useCallback(() => {
    const instance = confettiRef.current;
    if (!instance || animationHasFired) return;
    const numOfConfetti = width >= 979 ? 200 : 300;
    Array.from({
      length: numOfConfetti
    }).forEach(() => instance(getConfettiConfig()));
    setAnimationHasFired(true);
  }, [animationHasFired, setAnimationHasFired, getConfettiConfig, width]);
  useEffect(() => {
    if (!welcomeMessageIsVisible || !canvasRef.current) return;
    plusFlagRef.current = confetti.shapeFromPath({
      path: "M133.859 77.4912H0L13.3858 18.6198C15.8957 7.66697 25.6562 0 37.09 0H170.949L157.563 58.8714C155.053 69.8242 145.014 77.4912 133.859 77.4912Z"
      // path: "M 50,5 95,97.5 5,97.5 z", // ex: triangle
      // matrix: new DOMMatrix([0.15, 0, 0, 0.15, -85, -38]), // example mmatrix to scale and position
    });
    confettiRef.current = confetti.create(canvasRef.current, {
      resize: false,
      useWorker: false
    });
    fireConfetti();
  }, [welcomeMessageIsVisible, fireConfetti]);

  // because we are using absolute positioning for the content...
  // (else button ends up under the canvas - which creates it's own stacking context),
  // we cant rely on flex, align-center, etc so we need to calculate
  // the bottom position dynamically and use the modal's height and overflow
  // to calc the position the content for the most reliable results across breakpoints and resizing
  const {
    dynamicBottomPosition,
    contentHeight
  } = useMemo(() => {
    if (width >= 979) {
      return {
        contentHeight: "500px",
        dynamicBottomPosition: `${modalInfo.bottomOverflow + (modalInfo.height - 500) / 2}px`
      };
    }
    if (width >= 600) {
      const h = modalInfo.height / 2;
      return {
        contentHeight: `${h}px`,
        dynamicBottomPosition: `${modalInfo.bottomOverflow + modalInfo.height * 0.25}px`
      };
    }
    // for smallest screensizes we add 64px to height for better centering to the center
    return {
      contentHeight: `${modalInfo.height / 2 + 64}px`,
      dynamicBottomPosition: `${modalInfo.bottomOverflow + 30}px`
    };
  }, [modalInfo, width]);
  if (!welcomeMessageIsVisible) return null;
  const gradientStyle = width >= 979 ? "radial-gradient(circle at 50% 37%, rgb(21, 90, 122) 14%, rgb(6, 54, 79) 37%, rgb(6 45 66) 52%, rgb(2, 46, 68) 100%)" : "radial-gradient(circle at 50% 60%, rgb(21, 90, 122) 7%, rgb(6, 54, 79) 41%, rgb(6, 45, 66) 52%, rgb(2, 46, 68) 100%)";
  return <div ref={wrapperRef} data-testid="welcome-message-wrapper" style={{
    backgroundImage: gradientStyle
  }} className={cls("relative w-full h-full overflow-y-auto overflow-x-hidden")} data-sentry-component="WelcomeMessage" data-sentry-source-file="WelcomeMessage.tsx">
      {/* Confetti canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-0" />

      {/* Content wrapper helps keep things w/in modalviewport */}
      <div className={"grid grid-col-2 absolute w-full"} style={{
      bottom: dynamicBottomPosition,
      height: contentHeight
    }}>
        {/* Header */}
        <div className="text-center">
          <PlusLink href="javascript:void(0)" className="pointer-events-none" iconProps={{
          style: {
            fill: "currentColor",
            height: "70px",
            width: "117px"
          }
        }} data-sentry-element="PlusLink" data-sentry-source-file="WelcomeMessage.tsx" />
          <div className="text-white mb-5 lm:mb-0">
            <p className={cls("font-bold text-[40px] leading-[2.25rem] mb-2 w-full", "lm:text-3xl lm:mb-0")}>
              Welcome to Plus!
            </p>
            <p className="lm:mb-0">
              {width >= 979 ? "Enjoy unlimited access to these benefits:" : "The Savings Start Today"}
            </p>
          </div>
        </div>

        {/* Icons */}
        <div className="hidden lm:flex flex-row justify-center gap-12 mt-6 lm:mt-4">
          {[{
          src: "/uploads/design/2024/6/PlusPage/free-shipping.svg",
          label: ["Fast and Free", "Shipping"],
          key: "icon-1"
        }, {
          src: "/uploads/design/2024/6/PlusPage/pricing.svg",
          label: ["Exclusive", "Member Pricing"],
          key: "icon-2"
        }, {
          src: "/uploads/design/2024/6/PlusPage/guaranteed-savings.svg",
          label: ["Guaranteed", "Savings"],
          key: "icon-3"
        }].map(({
          src,
          label,
          key
        }) => <div key={key} className="flex flex-col items-center w-[115px]">
              <div className="flex items-center justify-center h-28 w-28 rounded-full bg-[#F0FAFE]">
                <Image alt="" src={src} width={100} height={69} />
              </div>
              <div className="text-white w-40 text-center mt-4">
                <p className="m-0">{label[0]}</p>
                <p>{label[1]}</p>
              </div>
            </div>)}
        </div>

        <div className={cls("self-end px-6", "xs:max-w-[250px] xs:justify-self-center", "lm:mt-12")}>
          <Button theme="wss" variant="secondary" color="white" size="large" className="w-full" onClick={onClose} data-testid="dismiss-welcome-button" data-sentry-element="Button" data-sentry-source-file="WelcomeMessage.tsx">
            Start Saving with Plus
          </Button>
        </div>
      </div>
    </div>;
};
export default WelcomeMessage;